import type { SpellFx } from '@shefira/shared';
import type Phaser from 'phaser';

import {
  animate,
  behind,
  dot,
  EMBER,
  EMBER_DARK,
  fadeAfter,
  type Feet,
  GOLD,
  GOLD_TIP,
  ORANGE,
  over,
  WHITE,
} from './fx-kit.js';
import {
  charge,
  execute,
  rage,
  rend,
  shieldBash,
  shieldWall,
  taunt,
} from './spell-effects-grimoire.js';
import { seismic, titan } from './spell-effects-ground.js';
import { puffSmoke } from './smoke-puff.js';

export type { Feet } from './fx-kit.js';

/** Where a spell's effect is drawn: on its caster, and on its target for a strike. */
export interface SpellFxPlace {
  caster: Feet;
  target?: Feet;
  /** Where a charge set the caster down; `caster` is then where they stood. */
  leapTo?: Feet;
  /** The tiles a line spell swept, in order from the caster. */
  line?: Feet[];
  /** How far an area spell reaches, in tiles: its range. */
  reach?: number;
}

/**
 * The look of each spell as it goes off, drawn from code like the mount's
 * puff of dust: a counter runs from 0 to 1 and each frame the effect is
 * redrawn in whole 1×1 pixels at rounded positions (CLAUDE.md rule 6), in
 * palette colours. Purely visual: the server already resolved the spell and
 * its numbers arrive on their own.
 */
export function playSpellFx(scene: Phaser.Scene, fx: SpellFx, place: SpellFxPlace): void {
  switch (fx) {
    case 'strike':
      strike(scene, place.target ?? place.caster);
      return;
    case 'whirlwind':
      whirlwind(scene, place.caster);
      return;
    case 'war-cry':
      warCry(scene, place.caster);
      return;
    case 'charge':
      charge(scene, place.caster, place.leapTo, place.target);
      return;
    case 'rend':
      rend(scene, place.target ?? place.caster);
      return;
    case 'shield-wall':
      shieldWall(scene, place.caster);
      return;
    case 'shield-bash':
      shieldBash(scene, place.target ?? place.caster);
      return;
    case 'taunt':
      taunt(scene, place.caster);
      return;
    case 'seismic':
      seismic(scene, place.caster, place.line ?? []);
      return;
    case 'rage':
      rage(scene, place.caster);
      return;
    case 'execute':
      execute(scene, place.target ?? place.caster);
      return;
    case 'titan':
      titan(scene, place.caster, place.reach ?? 2);
      return;
  }
}

/**
 * Power strike, on the target: a slash cut across it corner to corner, then a
 * burst of golden sparks from where it bit. Short and bright: it is a blow.
 */
function strike(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 11;
  const rays = 8;
  const tilt = Math.random() * Math.PI;
  animate(scene, over(feet), 400, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.55));

    // The cut: drawn in over the first third, from top right to bottom left.
    const reach = Math.min(1, p / 0.3);
    const half = 9;
    const length = Math.round(half * 2 * reach);
    for (let step = 0; step <= length; step += 1) {
      const t = step / (half * 2);
      const x = cx + half - t * half * 2;
      const y = cy - half + t * half * 2;
      const tip = step >= length - 2;
      dot(graphics, x, y, tip ? WHITE : GOLD_TIP);
      dot(graphics, x + 1, y, tip ? GOLD_TIP : GOLD);
    }

    // A white flash at the point of impact, the moment the cut lands.
    if (p > 0.2 && p < 0.4) {
      graphics.fillStyle(WHITE, 1);
      graphics.fillRect(cx - 1, cy - 1, 3, 3);
    }

    // Sparks: short rays flying out from the centre, cooling as they go.
    if (p > 0.2) {
      const q = (p - 0.2) / 0.8;
      const inner = 3 + q * 10;
      const tail = Math.max(1, Math.round(4 * (1 - q)));
      const color = q < 0.35 ? GOLD_TIP : q < 0.7 ? GOLD : ORANGE;
      for (let ray = 0; ray < rays; ray += 1) {
        const angle = tilt + (ray / rays) * Math.PI * 2;
        // Every other ray shorter: a star, not a wheel.
        const scale = ray % 2 === 0 ? 1 : 0.65;
        for (let step = 0; step < tail; step += 1) {
          const r = (inner + step) * scale;
          dot(graphics, cx + Math.cos(angle) * r, cy + Math.sin(angle) * r * 0.8, color);
        }
      }
    }
  });
}

/**
 * Whirlwind, around the caster: three streaks of fire sweep round them one
 * and a half turns, out to the neighbouring tiles it strikes, as the spell's
 * icon shows it. Flames lick upward off each streak and flicker every frame;
 * embers drift up once it has passed. The half of the circle in front of the
 * body is drawn over it, the far half behind it, so the fire goes round the body.
 */
function whirlwind(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 5;
  const streaks = 3;
  const trail = 1.3;
  const duration = 560;
  const start = Math.random() * Math.PI * 2;
  const drawHalf = (front: boolean) => (graphics: Phaser.GameObjects.Graphics, p: number) => {
    graphics.setAlpha(fadeAfter(p, 0.7));
    const head = start + p * Math.PI * 3;
    // Swells from the body out to the next tile, flattened like the ground.
    const rx = 8 + 9 * Math.min(1, p / 0.4);
    const ry = rx * 0.5;
    for (let streak = 0; streak < streaks; streak += 1) {
      const base = head + (streak / streaks) * Math.PI * 2;
      for (let a = 0; a <= trail; a += 0.07) {
        const angle = base - a;
        const sin = Math.sin(angle);
        if (sin >= 0 !== front) continue;
        const share = a / trail;
        const x = cx + Math.cos(angle) * rx;
        const y = cy + sin * ry;
        // The streak itself: white-hot at its head, burning down to embers behind.
        dot(graphics, x, y, flameColor(share));
        // A tongue of flame above it, tallest near the head, a new height each
        // frame so the fire flickers rather than slides.
        const height = Math.round((1 - share) * 5 * (0.4 + Math.random() * 0.6));
        for (let rise = 1; rise <= height; rise += 1) {
          const lean = rise > 2 && Math.random() < 0.3 ? (Math.random() < 0.5 ? -1 : 1) : 0;
          dot(
            graphics,
            x + lean,
            y - rise,
            flameColor(share + (rise / height) * (1 - share) * 0.8),
          );
        }
      }
    }
  };
  animate(scene, behind(feet), duration, drawHalf(false));
  animate(scene, over(feet), duration, drawHalf(true));
  riseEmbers(scene, feet, duration);
  puffSmoke(scene, { x: Math.round(feet.x), y: Math.round(feet.y) }, over(feet));
}

/** Fire along a flame, from 0 (hottest) to 1 (dying): white, yellow, orange, red, soot. */
function flameColor(heat: number): number {
  if (heat < 0.1) return GOLD_TIP;
  if (heat < 0.3) return GOLD;
  if (heat < 0.55) return ORANGE;
  if (heat < 0.8) return EMBER;
  return EMBER_DARK;
}

/** Sparks thrown off the ring as it turns, drifting up and out as they cool. */
function riseEmbers(scene: Phaser.Scene, feet: Feet, afterMs: number): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 5;
  const embers = Array.from({ length: 10 }, () => {
    const angle = Math.random() * Math.PI * 2;
    return {
      x: cx + Math.cos(angle) * 15,
      y: cy + Math.sin(angle) * 7,
      drift: Math.cos(angle) * (2 + Math.random() * 3),
      rise: 10 + Math.random() * 10,
      delay: Math.random() * 0.45,
    };
  });
  animate(scene, over(feet), afterMs + 300, (graphics, p) => {
    for (const ember of embers) {
      const q = (p - ember.delay) / (1 - ember.delay);
      if (q <= 0 || q >= 1) continue;
      dot(graphics, ember.x + ember.drift * q, ember.y - ember.rise * q, flameColor(0.2 + q * 0.8));
    }
  });
}

/**
 * War cry, on the caster: a golden shock ring spreads over the ground from
 * their feet, and sparks rise around the body as the breath comes back.
 */
function warCry(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 2;

  // The ring, on the ground: under the body behind, over it in front.
  const ring = (front: boolean) => (graphics: Phaser.GameObjects.Graphics, p: number) => {
    graphics.setAlpha(fadeAfter(p, 0.4));
    const rx = 3 + p * 20;
    const ry = rx * 0.45;
    const color = p < 0.3 ? GOLD_TIP : p < 0.65 ? GOLD : ORANGE;
    const steps = Math.max(16, Math.round(rx * 5));
    for (let step = 0; step < steps; step += 1) {
      const angle = (step / steps) * Math.PI * 2;
      const sin = Math.sin(angle);
      if (sin >= 0 !== front) continue;
      dot(graphics, cx + Math.cos(angle) * rx, cy + sin * ry, color);
    }
  };
  animate(scene, behind(feet), 560, ring(false));
  animate(scene, over(feet), 560, ring(true));

  // Sparks: little crosses rising up the body, each on its own delay, twinkling.
  const sparks = Array.from({ length: 9 }, (_, index) => ({
    x: cx + Math.round(Math.cos((index / 9) * Math.PI * 2) * (5 + Math.random() * 4)),
    y: Math.round(feet.y) - 2 - Math.round(Math.random() * 10),
    rise: 12 + Math.random() * 8,
    delay: Math.random() * 0.35,
  }));
  animate(scene, over(feet), 800, (graphics, p) => {
    for (const spark of sparks) {
      const q = (p - spark.delay) / (1 - spark.delay);
      if (q <= 0 || q >= 1) continue;
      const y = spark.y - spark.rise * q;
      const bright = Math.floor(q * 12) % 3 !== 0;
      const color = q > 0.7 ? EMBER : bright ? GOLD_TIP : GOLD;
      dot(graphics, spark.x, y, color);
      // Full crosses while fresh, single pixels as they die out.
      if (q < 0.6 && bright) {
        dot(graphics, spark.x - 1, y, GOLD);
        dot(graphics, spark.x + 1, y, GOLD);
        dot(graphics, spark.x, y - 1, GOLD);
        dot(graphics, spark.x, y + 1, GOLD);
      }
    }
  });
}
